/**
 * Staff coaching ("AI as office manager" for the team, plan of 2026-11-02:
 * "Running the team for him"). Phase 1 grades the front desk on the calls
 * Velora already records, transcribes and reviews under the OpenAI BAA; the
 * other roles (treatment coordinator, hygienist, assistant, billing) slot in
 * later through the same profile, card and habit shapes.
 *
 * Fairness rules (plan "Grade the moment, not the person's luck"):
 * - Only real chances count: a QA part the call gave no chance to show is
 *   null in the review and never counts, and calls the review marked not
 *   relevant (wrong numbers, vendors) never count.
 * - Behaviour (what the person did) and results (what happened) are shown side
 *   by side; the headline grade is behaviour only.
 * - Compared with themselves first (last week, the four weeks before), then
 *   with the office average. No leaderboards: a person only ever sees their
 *   own card; an office lead sees their office, one person at a time.
 * - Fewer than MIN_CHANCES chances: no grade, "not enough yet".
 * - Every grade carries its receipts: the calls it came from, with the moment
 *   in the recording.
 * - "I disagree" sends a graded call to the office lead; an overturned grade
 *   is left out of every card and kept as feedback for the AI review.
 * - Once a month the office lead grades five calls blind; agreement per
 *   office is shown.
 *
 * Velora only informs: nothing here changes anyone's pay, schedule or job.
 * Routes live under `/v1/coaching`.
 */
import { z } from 'zod';
import { letterGrade, QA_PARTS, QaScorecardSchema, type QaPart } from './phones-ai.js';
import { RatioSchema } from './phones.js';

const Uuid = z.uuid();
const Iso = z.iso.datetime({ offset: true });
const IsoDate = z.iso.date();
const Count = z.number().int().nonnegative();

/** Live-refresh entity key. */
export const COACHING_ENTITY = 'coaching';

/** No grade on fewer chances than this ("not enough yet"). */
export const MIN_CHANCES = 10;
/** A part scored at or below this (of 5) on a call is a miss of that habit. */
export const MISS_AT_OR_BELOW = 2;
/** A part scored at or above this (of 5) is a strong moment (wins, coworker examples). */
export const STRONG_PART_SCORE = 5;
/** A habit counts as improved when the part's score rose this much, or reached HABIT_DONE_SCORE. */
export const HABIT_IMPROVED_POINTS = 5;
export const HABIT_DONE_SCORE = 85;
/** Coaching weeks a habit may stay unimproved before the office lead is asked for a 1:1. */
export const HABIT_STUCK_WEEKS = 3;
/** Calls the office lead grades blind each month. */
export const CALIBRATION_CALLS = 5;
/** A human and the AI agree on a part when they are this close (of 5). */
export const CALIBRATION_AGREE_WITHIN = 1;

/* ------------------------------------------------------------------------ */
/* Roles and profiles                                                        */
/* ------------------------------------------------------------------------ */

export const COACHING_ROLES = [
  'front_desk',
  'office_lead',
  'treatment_coordinator',
  'hygienist',
  'assistant',
  'billing',
  'doctor',
] as const;
export const CoachingRoleSchema = z.enum(COACHING_ROLES);
export type CoachingRole = z.infer<typeof CoachingRoleSchema>;

export const COACHING_ROLE_LABEL: Record<CoachingRole, string> = {
  front_desk: 'Front desk',
  office_lead: 'Office lead',
  treatment_coordinator: 'Treatment coordinator',
  hygienist: 'Hygienist',
  assistant: 'Assistant',
  billing: 'Billing',
  doctor: 'Doctor',
};

/** Roles graded on calls today (phase 1). The others get a card once their source is built. */
export const CALL_GRADED_ROLES: readonly CoachingRole[] = ['front_desk', 'office_lead', 'billing'];
/** Roles with a weekly card: the call roles plus the chair roles (hygienist, assistant). */
export const CARD_ROLES: readonly CoachingRole[] = [
  ...CALL_GRADED_ROLES,
  'hygienist',
  'assistant',
  'treatment_coordinator',
  'doctor',
];

/**
 * Where Velora hears each role (Zac 2026-11-03): the phone roles answer a
 * desk phone; the chair roles may later have an operatory microphone (chair
 * pilot), whose absence is never "missing"; any role may be "PMS data only".
 * `required` drives Setup's "Still missing" list.
 */
export const PHONE_ROLES: readonly CoachingRole[] = [
  'front_desk',
  'billing',
  'treatment_coordinator',
  'office_lead',
];
export const CHAIR_ROLES: readonly CoachingRole[] = ['hygienist', 'assistant', 'doctor'];
export type ProfileLink = 'desk' | 'pms' | 'login';
export const ROLE_REQUIRED_LINKS: Record<CoachingRole, readonly ProfileLink[]> = {
  front_desk: ['desk', 'pms', 'login'],
  billing: ['desk', 'pms', 'login'],
  treatment_coordinator: ['desk', 'pms', 'login'],
  office_lead: ['desk', 'pms', 'login'],
  hygienist: ['pms', 'login'],
  assistant: ['pms', 'login'],
  doctor: ['pms', 'login'],
};
/** Plain words for a missing required link (Setup's "Still missing"). */
export const LINK_GAP_LABEL: Record<ProfileLink, string> = {
  desk: 'No desk phone',
  pms: 'No PMS user',
  login: 'No Velora login (no morning note)',
};

/** "Still missing": only the links the role needs; a PMS-only profile needs no desk (pure). */
export function requiredGaps(p: {
  role: CoachingRole;
  hasDesk: boolean;
  hasPms: boolean;
  hasLogin: boolean;
  pmsOnly: boolean;
}): string[] {
  const out: string[] = [];
  for (const l of ROLE_REQUIRED_LINKS[p.role]) {
    if (l === 'desk' && !p.hasDesk && !p.pmsOnly) out.push(LINK_GAP_LABEL.desk);
    if (l === 'pms' && !p.hasPms) out.push(LINK_GAP_LABEL.pms);
    if (l === 'login' && !p.hasLogin) out.push(LINK_GAP_LABEL.login);
  }
  return out;
}

export const ProfileExtensionSchema = z.object({
  locationId: Uuid,
  extension: z.string().regex(/^[0-9]{2,6}$/),
});
export type ProfileExtension = z.infer<typeof ProfileExtensionSchema>;

export const CoachingProfileSchema = z.object({
  id: Uuid,
  locationId: Uuid,
  officeName: z.string(),
  displayName: z.string(),
  role: CoachingRoleSchema,
  roleLabel: z.string(),
  active: z.boolean(),
  userId: Uuid.nullable(),
  userName: z.string().nullable(),
  timeclockStaffId: Uuid.nullable(),
  timeclockName: z.string().nullable(),
  pmsStaffId: Uuid.nullable(),
  pmsStaffName: z.string().nullable(),
  extensions: z.array(
    ProfileExtensionSchema.extend({ officeName: z.string(), name: z.string().nullable() }),
  ),
  /** Their strong calls may be played to coworkers as examples. */
  exampleOptIn: z.boolean(),
  /** Email override for the Friday card (else the login's email). Managers only. */
  email: z.string().nullable(),
  /** What is not linked yet, in plain words: only the links the role needs (empty when complete). */
  gaps: z.array(z.string()),
  /** Optional sources shown in neutral grey, never as missing ("Chair recording: not set up"). */
  optional: z.array(z.string()).default([]),
  /** Nothing is recorded: graded from PMS data only. */
  pmsOnly: z.boolean().default(false),
  /** Operatories with a chair microphone that are this person's room (chair pilot). */
  chairRooms: z
    .array(z.object({ operatoryId: Uuid, name: z.string(), officeName: z.string() }))
    .default([]),
  /** Days clocked in during the last 14 days (time clock), null without a clock link. */
  shiftDays14: Count.nullable(),
});
export type CoachingProfile = z.infer<typeof CoachingProfileSchema>;

export const ExtensionSuggestionSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  extension: z.string(),
  /** The name the phone system shows for the desk. */
  name: z.string().nullable(),
  /** Answered and placed calls on this desk in the last 28 days. */
  calls28: Count,
  /** A profile that already holds this desk (the first, when it is shared). */
  profileId: Uuid.nullable(),
  /** People linked to this desk (more than one: a shared desk, the time clock decides). */
  people: Count,
  /** Busy enough that several people must answer on it (one desk name for everyone): add each person to it. */
  likelyShared: z.boolean(),
  /** Best guesses for a person (same first name at the office). */
  userId: Uuid.nullable(),
  userName: z.string().nullable(),
  pmsStaffId: Uuid.nullable(),
  pmsStaffName: z.string().nullable(),
});
export type ExtensionSuggestion = z.infer<typeof ExtensionSuggestionSchema>;

export const ProfilesResponseSchema = z.object({
  profiles: z.array(CoachingProfileSchema),
  suggestions: z.array(ExtensionSuggestionSchema),
  /** Pickers for the link editor (people of the offices in view). */
  users: z.array(z.object({ id: Uuid, name: z.string() })),
  pmsStaff: z.array(z.object({ id: Uuid, locationId: Uuid, name: z.string() })),
  timeclockStaff: z.array(z.object({ id: Uuid, name: z.string(), userId: Uuid.nullable() })),
  /** Operatories with a Scribe chair microphone bound (chair pilot); empty: none set up yet. */
  chairRooms: z
    .array(
      z.object({
        operatoryId: Uuid,
        locationId: Uuid,
        officeName: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
  canManage: z.boolean(),
});
export type ProfilesResponse = z.infer<typeof ProfilesResponseSchema>;

export const ProfileUpsertSchema = z.object({
  locationId: Uuid,
  displayName: z.string().trim().min(1).max(120),
  role: CoachingRoleSchema,
  active: z.boolean().default(true),
  userId: Uuid.nullable().default(null),
  timeclockStaffId: Uuid.nullable().default(null),
  pmsStaffId: Uuid.nullable().default(null),
  extensions: z.array(ProfileExtensionSchema).max(10).default([]),
  email: z.email().max(200).nullable().default(null),
  /** Nothing recorded: grade from PMS data only (desk phones and rooms are then cleared). */
  pmsOnly: z.boolean().default(false),
  /** Chair-microphone operatories that are this person's room (optional). */
  chairOperatoryIds: z.array(Uuid).max(10).default([]),
});
export type ProfileUpsert = z.infer<typeof ProfileUpsertSchema>;

/** One click: a profile for each desk that has a name and no profile yet. */
export const ProfileAutoCreateSchema = z.object({
  extensions: z.array(ProfileExtensionSchema).min(1).max(100),
});

export const ExampleOptInSchema = z.object({ optIn: z.boolean() });

/* ------------------------------------------------------------------------ */
/* Report card                                                               */
/* ------------------------------------------------------------------------ */

/** Plain-language habit names (what the Help Center lessons are called). */
export const PART_HABIT: Record<QaPart, string> = {
  greeting: 'A warm greeting',
  listening: 'Finding the need',
  resolution: 'Solving the need',
  booking: 'Offering times first',
  closing: 'A clear next step',
};

/** The Help Center lesson for each part (docs/help/coaching-<part>.md). */
export const PART_LESSON: Record<QaPart, string> = {
  greeting: 'coaching-greeting',
  listening: 'coaching-listening',
  resolution: 'coaching-resolution',
  booking: 'coaching-booking',
  closing: 'coaching-closing',
};

/** The habit to try, as one plain sentence (Friday email, huddle note). */
export const PART_TRY: Record<QaPart, string> = {
  greeting:
    'Try answering with the office name and your own name, then an offer to help, like "Thank you for calling, this is Ashley, how can I help you today?"',
  listening:
    'Try saying the reason back before you answer, like "So the crown on the left is bothering you again, is that right?"',
  resolution:
    'Try checking it is settled before you hang up, like "Did that answer everything, or is there anything else I can help with?"',
  booking:
    'Try offering two times right away, like "I have Tuesday at 9 or Thursday at 2, which works better?"',
  closing:
    'Try ending with what happens next, like "You are all set for Tuesday at 9, and we will text you a reminder the day before."',
};

/** "On N calls ..." for the Friday email: what a miss of the part looks like. */
export const PART_MISS: Record<QaPart, string> = {
  greeting: 'the greeting did not give the office name or offer help',
  listening: 'we moved on before we were sure what the caller needed',
  resolution: "the caller's question was not fully settled before the call ended",
  booking: "the patient asked about an appointment and we didn't offer a time",
  closing: 'the call ended without a clear next step',
};

export const ReceiptSchema = z.object({
  callId: Uuid,
  locationId: Uuid,
  at: Iso,
  /** The part's score on this call (0-5). */
  score: z.number().int().min(0).max(5),
  /** Where in the recording the moment starts (ms), from the transcript; null without one. */
  momentMs: z.number().int().nonnegative().nullable(),
  hasRecording: z.boolean(),
  /** An "I disagree" on this call is open or decided. */
  dispute: z.enum(['open', 'upheld', 'overturned']).nullable(),
});
export type Receipt = z.infer<typeof ReceiptSchema>;

export const TrendSchema = z.enum(['up', 'down', 'flat']);

export const BehaviourRowSchema = z.object({
  part: z.enum(QA_PARTS),
  label: z.string(),
  habit: z.string(),
  /** Calls that gave a real chance to show this part. */
  chances: Count,
  /** Chances scored at or below MISS_AT_OR_BELOW. */
  misses: Count,
  /** 0-100; null under MIN_CHANCES ("not enough yet"). */
  score: z.number().min(0).max(100).nullable(),
  grade: z.string().nullable(),
  /** Their own score last week and over the four weeks before (null when too few). */
  lastWeek: z.number().min(0).max(100).nullable(),
  fourWeeks: z.number().min(0).max(100).nullable(),
  trend: TrendSchema.nullable(),
  /** Everyone at the office, same week (null when too few). */
  officeAverage: z.number().min(0).max(100).nullable(),
  /** The calls behind the grade, weakest first (at most 5). */
  receipts: z.array(ReceiptSchema),
  lesson: z.string(),
});
export type BehaviourRow = z.infer<typeof BehaviourRowSchema>;

export const ResultRowSchema = z.object({
  key: z.enum([
    'answered_open',
    'new_patient_booked',
    'booking_intent_booked',
    'booked_on_call',
    'missed_on_shift',
    'missed_clocked_in',
    'callers_happy',
  ]),
  label: z.string(),
  ratio: RatioSchema.nullable(),
  count: Count.nullable(),
  /** "94%", "7 of 9", "3". */
  display: z.string(),
  /** Letter grade for ratios with at least MIN_CHANCES in the denominator. */
  grade: z.string().nullable(),
  note: z.string().nullable(),
});
export type ResultRow = z.infer<typeof ResultRowSchema>;

export const HABIT_STATUSES = ['active', 'improved', 'not_yet', 'not_enough', 'replaced'] as const;
export const HabitStatusSchema = z.enum(HABIT_STATUSES);
export type HabitStatus = z.infer<typeof HabitStatusSchema>;

export const HabitSchema = z.object({
  id: Uuid,
  part: z.enum(QA_PARTS),
  habit: z.string(),
  tryText: z.string(),
  lesson: z.string(),
  /** Monday of the week the habit was chosen from. */
  fromWeek: IsoDate,
  baseline: z.number().min(0).max(100).nullable(),
  baselineChances: Count,
  status: HabitStatusSchema,
  /** Weeks checked since it was chosen without improving. */
  weeksStuck: Count,
  checkedWeek: IsoDate.nullable(),
  checkedScore: z.number().min(0).max(100).nullable(),
  checkedChances: Count.nullable(),
});
export type Habit = z.infer<typeof HabitSchema>;

/* ------------------------------------------------------------------------ */
/* Front-desk pillars (Zac 2026-11-02): phones, booking, saving the          */
/* schedule, checkout, plus an accuracy line                                 */
/* ------------------------------------------------------------------------ */

export const PILLAR_KEYS = [
  'phones',
  'booking',
  'saving',
  'checkout',
  'accuracy',
  // Hygienists and assistants (chair roles).
  'on_time',
  'notes',
  'care',
  'education',
  // Doctors (graded; seen only by the doctor and all-office viewers).
  'production',
  'acceptance',
  // Treatment coordinator and billing (role scorecards, 2026-11-03).
  'followup',
  'claims',
] as const;
export type PillarKey = (typeof PILLAR_KEYS)[number];
export const PILLAR_LABEL: Record<PillarKey, string> = {
  phones: 'Phones',
  booking: 'Booking',
  saving: 'Saving the schedule',
  checkout: 'Checkout',
  accuracy: 'Accuracy',
  on_time: 'Running on time',
  notes: 'Notes on time',
  care: 'Care follow-through',
  education: 'Explaining treatment',
  production: 'Production per patient',
  acceptance: 'Case acceptance',
  followup: 'Treatment follow-up',
  claims: 'Claims and payments',
};

/**
 * - ok: graded on enough chances;
 * - not_enough: some chances, fewer than MIN_CHANCES (no grade);
 * - waiting: the PMS does not tell us who did it yet ("waiting on Dentrix /
 *   Open Dental data"), never shown as zero;
 * - needs_clock: a per-hour number without time clock hours.
 */
export const MetricStateSchema = z.enum([
  'ok',
  'not_enough',
  'waiting',
  'needs_clock',
  /** One PMS login for many people (Zand): per-person PMS grades are not possible. */
  'shared_login',
  /** Needs the consented chair recording (not built yet). */
  'needs_recording',
  /** A role-scorecard line whose source is not in Velora yet: "Coming: needs <source>", never a zero. */
  'coming',
]);
export type MetricState = z.infer<typeof MetricStateSchema>;

export const PillarMetricSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Behaviour (in the person's control) or result (partly not). */
  kind: z.enum(['behaviour', 'result', 'volume', 'flag']),
  state: MetricStateSchema,
  /** 0-100 for rates; a count or a per-hour figure for volume; null when not shown. */
  value: z.number().nullable(),
  ratio: RatioSchema.nullable(),
  display: z.string(),
  grade: z.string().nullable(),
  officeAverage: z.number().nullable(),
  /** Their own four weeks before (rates and per-hour figures). */
  fourWeeks: z.number().nullable(),
  note: z.string().nullable(),
  /** Appointments behind a flag or a rate (at most 5, opened in the schedule). */
  appointmentIds: z.array(Uuid),
  /** The list behind the line (Analytics, Case Closed, Claims, Phones), office-scoped. */
  href: z.string().nullable().optional(),
});
export type PillarMetric = z.infer<typeof PillarMetricSchema>;

export const PillarSchema = z.object({
  key: z.enum(PILLAR_KEYS),
  label: z.string(),
  state: MetricStateSchema,
  /** Weighted toward behaviour (see coaching/lib/pillars.ts); null without enough chances. */
  score: z.number().min(0).max(100).nullable(),
  grade: z.string().nullable(),
  trend: TrendSchema.nullable(),
  officeAverage: z.number().nullable(),
  metrics: z.array(PillarMetricSchema),
  /** "Waiting on Dentrix data" and the like, when the PMS cannot say who did it yet. */
  waitingOn: z.string().nullable(),
});
export type Pillar = z.infer<typeof PillarSchema>;

/* ------------------------------------------------------------------------ */
/* Role scorecards (Zac 2026-11-03, ROLE-SCORECARDS.md)                      */
/* ------------------------------------------------------------------------ */

/**
 * Availability of a scorecard line:
 * - now: the data is in Velora today (computed);
 * - feed: needs the PMS appointment-history feed (who did it in Dentrix /
 *   Open Dental / Eaglesoft);
 * - assign: needs the Chairside morning pick (who is assisting whom);
 * - mic: needs operatory microphones (chair pilot).
 */
export const SCORE_AVAIL = ['now', 'feed', 'assign', 'mic'] as const;
export type ScoreAvail = (typeof SCORE_AVAIL)[number];
export const AVAIL_NEEDS: Record<Exclude<ScoreAvail, 'now'>, string> = {
  feed: 'the PMS appointment history (who did it)',
  assign: 'the Chairside morning pick (who you assist)',
  mic: 'operatory microphones (chair pilot)',
};

export interface ScorecardLine {
  /** The metric key on the card (pillar metric). */
  key: string;
  label: string;
  pillar: PillarKey;
  /** The chance (denominator): what counts as one try. */
  chance: string;
  source: string;
  avail: ScoreAvail;
  /** The evidence list (office-scoped by the card). */
  href: string | null;
}

const L = (
  key: string,
  label: string,
  pillar: PillarKey,
  chance: string,
  source: string,
  avail: ScoreAvail,
  href: string | null,
): ScorecardLine => ({ key, label, pillar, chance, source, avail, href });

/**
 * Every role's scorecard: what the person controls, from what they actually do
 * in the PMS or Velora (clinical judgement is never graded). Each line names
 * its chance; no chances means not graded, never zero. Lines whose source is
 * not in Velora yet show "Coming: needs <source>".
 */
export const ROLE_SCORECARDS: Record<CoachingRole, readonly ScorecardLine[]> = {
  front_desk: [
    L(
      'call_behaviour',
      'Call quality (greet, need, offer a time, next step)',
      'phones',
      'answered calls',
      'callvault AI review',
      'now',
      '/phones?tab=analytics',
    ),
    L(
      'answered_open',
      'Answered while on shift',
      'phones',
      'open-hours calls ringing their desk while clocked in',
      'phones + time clock',
      'now',
      '/phones?tab=analytics&facet=missed',
    ),
    L(
      'new_patients_booked',
      'New-patient calls booked',
      'booking',
      'new-patient calls answered',
      'phones + call outcome',
      'now',
      '/phones?tab=analytics&facet=new_patient',
    ),
    L(
      'texts_answered',
      'Patient texts answered within 24 hours',
      'phones',
      'patient texts they replied to',
      'messaging (replied by)',
      'now',
      '/messages',
    ),
    L(
      'insurance_checked',
      'Insurance checks run before the visit',
      'accuracy',
      "the office's insured visits this week",
      'eligibility checks (run by)',
      'now',
      '/insurance',
    ),
    L(
      'booked_per_hour',
      'Appointments booked per hour on shift',
      'booking',
      'hours on shift',
      'appointment history',
      'feed',
      '/analytics/schedule',
    ),
    L(
      'rebooked',
      'Saves: cancels rebooked within 2 days',
      'saving',
      'cancels they handled',
      'appointment history',
      'feed',
      '/analytics/schedule',
    ),
    L(
      'broken_no_rebook',
      'Broken left with no rebook',
      'saving',
      'breaks they entered',
      'appointment history',
      'feed',
      '/analytics/schedule',
    ),
    L(
      'confirmed_day_before',
      'Confirmations done by the day before',
      'saving',
      'next-day appointments at their office',
      'appointment history (confirmations)',
      'feed',
      '/analytics/huddle',
    ),
    L(
      'next_visit',
      'Next visit booked at checkout',
      'checkout',
      'patients they checked out',
      'appointment history',
      'feed',
      '/analytics/hygiene',
    ),
    L(
      'collected',
      'Collected at checkout vs patient portion',
      'checkout',
      'checkouts they did',
      'ledger (posted by)',
      'feed',
      '/analytics/money',
    ),
    L(
      'clean',
      'Accuracy: no $0, typed, broken not deleted',
      'accuracy',
      'appointments they created',
      'appointment history',
      'feed',
      '/analytics/schedule',
    ),
  ],
  office_lead: [
    L(
      'call_behaviour',
      'Call quality',
      'phones',
      'answered calls',
      'callvault AI review',
      'now',
      '/phones?tab=analytics',
    ),
    L(
      'disputes_on_time',
      'Disputes decided within 3 days',
      'followup',
      '"I disagree" requests at their office',
      'coaching disputes',
      'now',
      '/coaching?tab=reviews',
    ),
    L(
      'flags_on_time',
      'Coaching flags handled within 24 hours',
      'followup',
      'flags at their office',
      'coaching escalations',
      'now',
      '/coaching?tab=escalations',
    ),
    L(
      'calibration_done',
      'Monthly calibration done',
      'followup',
      'five sampled calls a month',
      'coaching calibration',
      'now',
      '/coaching?tab=calibration',
    ),
    L(
      'team_trend',
      'Team grade trend',
      'followup',
      'graded people at their office',
      'coaching cards',
      'now',
      '/council',
    ),
  ],
  treatment_coordinator: [
    L(
      'presented_scheduled',
      'Presented cases scheduled within 14 days',
      'acceptance',
      'cases presented at their office',
      'txplan + Case Closed',
      'now',
      '/case-closed',
    ),
    L(
      'touches',
      'Follow-up touches logged',
      'followup',
      'open cases in the queue',
      'Case Closed touches (by them)',
      'now',
      '/case-closed',
    ),
    L(
      'financing_offered',
      'Financing offered when cost was the objection',
      'followup',
      'cases flagged "cost"',
      'Case Closed financing steps (by them)',
      'now',
      '/case-closed',
    ),
    L(
      'same_day',
      'Same-day treatment converted',
      'acceptance',
      'same-day diagnoses at their office',
      'procedures planned and completed the same day',
      'now',
      '/case-closed/analytics',
    ),
    L(
      'call_behaviour',
      'Call quality',
      'phones',
      'answered calls',
      'callvault AI review',
      'now',
      '/phones?tab=analytics',
    ),
    L(
      'presentation',
      'Case presentation (why, image, cost, ask)',
      'education',
      'presentations recorded',
      'chair recordings',
      'mic',
      null,
    ),
  ],
  billing: [
    L(
      'claims_on_time',
      'Claims sent within 2 business days of the visit',
      'claims',
      'claims sent at their office',
      'claims (date sent vs visit)',
      'now',
      '/claims/work',
    ),
    L(
      'payment_links',
      'Payment links sent and paid',
      'claims',
      'links they sent',
      'payment links (sent by)',
      'now',
      '/billing/receivables',
    ),
    L(
      'call_behaviour',
      'Call quality on balance calls',
      'phones',
      'answered billing calls',
      'callvault AI review',
      'now',
      '/phones?tab=analytics',
    ),
    L(
      'first_pass',
      'First-pass acceptance; denials reworked in 7 days',
      'claims',
      'claims they sent',
      'claims / ERA (sent by)',
      'feed',
      '/claims/work',
    ),
    L(
      'era_posted',
      'ERA / EOB posted within 2 days',
      'claims',
      'payments received',
      'ledger (posted by)',
      'feed',
      '/analytics/money',
    ),
  ],
  hygienist: [
    L(
      'production_per_visit',
      'Production per patient visit',
      'production',
      'their visits',
      'procedures (provider)',
      'now',
      '/analytics/providers',
    ),
    L(
      'perio_charted',
      'Perio charted within the year',
      'care',
      'their adult cleaning visits',
      'perio exams',
      'now',
      '/analytics/hygiene',
    ),
    L(
      'bwx_due',
      'Bitewings within the year',
      'care',
      'their cleaning visits',
      'imaging + X-ray codes',
      'now',
      '/analytics/hygiene',
    ),
    L(
      'fluoride_kids',
      'Fluoride for children',
      'care',
      'their visits for patients under 19',
      'procedure codes',
      'now',
      '/analytics/hygiene',
    ),
    L(
      'perio_accepted',
      'Perio therapy scheduled after diagnosis',
      'care',
      'their perio diagnoses',
      'procedures planned to completed',
      'now',
      '/analytics/hygiene',
    ),
    L(
      'hygiene_rebook',
      'Next cleaning booked before the patient left',
      'care',
      'their completed cleaning visits',
      'appointments',
      'now',
      '/recall',
    ),
    L(
      'running_on_time',
      'Seated within 10 minutes',
      'on_time',
      'their appointments with a seated time',
      'schedule flow',
      'now',
      '/analytics/schedule',
    ),
    L(
      'notes_on_time',
      'Notes signed the same day',
      'notes',
      'their visits',
      'clinical notes',
      'now',
      '/analytics/hygiene',
    ),
    L(
      'findings_explained',
      'Explaining findings and home care',
      'education',
      'recorded visits',
      'chair recordings',
      'mic',
      null,
    ),
  ],
  assistant: [
    L(
      'notes_drafted',
      'Notes drafted on time',
      'notes',
      'notes they drafted',
      'clinical notes (drafted by)',
      'now',
      '/analytics/providers',
    ),
    L(
      'turnover',
      'Room turnover',
      'on_time',
      "their room's patient changes",
      'schedule flow',
      'assign',
      '/turnover',
    ),
    L(
      'running_on_time',
      'Running on time for their doctor',
      'on_time',
      "their doctor's appointments",
      'schedule flow',
      'assign',
      '/turnover',
    ),
    L(
      'next_step',
      'Next step scheduled before leaving',
      'care',
      'patients they dismissed',
      'appointments',
      'assign',
      '/turnover',
    ),
    L(
      'xrays_taken',
      'X-rays taken in Velora capture',
      'care',
      'their visits',
      'imaging (taken by)',
      'assign',
      '/turnover',
    ),
    L(
      'reinforce_plan',
      "Reinforcing the doctor's plan",
      'education',
      'recorded visits',
      'chair recordings',
      'mic',
      null,
    ),
  ],
  doctor: [
    L(
      'production_per_patient',
      'Production per patient visit',
      'production',
      'their visits',
      'procedures',
      'now',
      '/analytics/providers',
    ),
    L(
      'case_acceptance',
      'Case acceptance on their own diagnoses',
      'acceptance',
      'their planned treatment',
      'txplan to appointments',
      'now',
      '/case-closed/analytics',
    ),
    L(
      'exam_dx_rate',
      'Hygiene exam diagnosis rate',
      'acceptance',
      'exams they did',
      'procedures planned at exams',
      'now',
      '/analytics/providers',
    ),
    L(
      'same_day',
      'Same-day treatment conversion',
      'acceptance',
      'their same-day diagnoses',
      'procedures',
      'now',
      '/case-closed/analytics',
    ),
    L(
      'notes_24h',
      'Notes signed within 24 hours',
      'notes',
      'their visits with a note',
      'clinical notes',
      'now',
      '/analytics/providers',
    ),
    L(
      'running_on_time',
      'Running on time',
      'on_time',
      'their appointments with a seated time',
      'schedule flow',
      'now',
      '/analytics/schedule',
    ),
    L(
      'remakes',
      'Crowns redone within a year',
      'production',
      'their crowns and bridges',
      'procedures',
      'now',
      '/analytics/providers',
    ),
    L(
      'presentation',
      'Presenting treatment',
      'education',
      'recorded visits',
      'chair recordings',
      'mic',
      null,
    ),
  ],
};

/**
 * A role-scorecard line not on the card: "Coming: needs <source>" for feed,
 * assign and mic lines (pure). Never a zero, never graded.
 */
export function comingMetric(line: ScorecardLine): PillarMetric {
  const needs = line.avail === 'now' ? 'its first week of data' : AVAIL_NEEDS[line.avail];
  return {
    key: line.key,
    label: line.label,
    kind: 'behaviour',
    state: 'coming',
    value: null,
    ratio: null,
    display: `Coming: needs ${needs}`,
    grade: null,
    officeAverage: null,
    fourWeeks: null,
    note: `Chance: ${line.chance}. Source: ${line.source}.`,
    appointmentIds: [],
    href: line.href,
  };
}

/* ------------------------------------------------------------------------ */
/* Chairside morning pick (2026-11-03): who is assisting whom today           */
/* ------------------------------------------------------------------------ */

export const AssistPickSchema = z.object({
  providerId: Uuid.nullable(),
  providerName: z.string().nullable(),
  operatoryId: Uuid.nullable(),
  operatoryName: z.string().nullable(),
  /** self: they tapped it; inferred: Velora's guess waiting for a confirm; lead: set by the office lead. */
  source: z.enum(['self', 'inferred', 'lead']),
  confirmed: z.boolean(),
});
export type AssistPick = z.infer<typeof AssistPickSchema>;

export const AssistTodaySchema = z.object({
  /** False for people who are not assistants (nothing to show). */
  applies: z.boolean(),
  day: IsoDate,
  pick: AssistPickSchema.nullable(),
  /** Velora's guess (their last pick whose doctor works today, or the time clock), to confirm. */
  suggestion: AssistPickSchema.nullable(),
  /** Why Velora guessed it, in plain words. */
  why: z.string().nullable(),
  doctors: z.array(z.object({ id: Uuid, name: z.string() })),
  rooms: z.array(z.object({ id: Uuid, name: z.string() })),
});
export type AssistToday = z.infer<typeof AssistTodaySchema>;

export const AssistPutSchema = z
  .object({ providerId: Uuid.nullable(), operatoryId: Uuid.nullable() })
  .refine((x) => x.providerId !== null || x.operatoryId !== null, {
    message: 'Pick a doctor or a room.',
  });

/* ------------------------------------------------------------------------ */
/* Call tone (Zac 2026-11-02): its own section beside caller sentiment       */
/* ------------------------------------------------------------------------ */

export const TONE_KEYS = [
  'warmth',
  'empathy',
  'pace',
  'talk_share',
  'overtalk',
  'dead_air',
] as const;
export type ToneKey = (typeof TONE_KEYS)[number];

export const ToneMetricSchema = z.object({
  key: z.enum(TONE_KEYS),
  label: z.string(),
  /** Rates 0-100; pace in words a minute; talk-share %; overtalk per call; dead air seconds per call. */
  value: z.number().nullable(),
  display: z.string(),
  chances: Count,
  /** mono_only: needs the two-channel recording (3CX since 2026-10-26). */
  state: z.enum(['ok', 'not_enough', 'mono_only']),
  fourWeeks: z.number().nullable(),
  officeAverage: z.number().nullable(),
  /** Against their own four weeks before. */
  vsSelf: z.enum(['better', 'worse', 'same']).nullable(),
  grade: z.string().nullable(),
  note: z.string().nullable(),
});
export type ToneMetric = z.infer<typeof ToneMetricSchema>;

export const ToneTipSchema = z.object({
  text: z.string(),
  /** The words at that moment (PHI: shown only with patient access at the office). */
  quote: z.string().nullable(),
  callId: Uuid,
  atMs: z.number().int().nonnegative().nullable(),
});

export const ToneSectionSchema = z.object({
  score: z.number().min(0).max(100).nullable(),
  grade: z.string().nullable(),
  metrics: z.array(ToneMetricSchema),
  /** Calls with a two-channel recording (talk-share, pace, overtalk, dead air). */
  stereoCalls: Count,
  callerHappy: RatioSchema,
  best: z.object({ receipt: ReceiptSchema, why: z.string() }).nullable(),
  worst: z.object({ receipt: ReceiptSchema, why: z.string() }).nullable(),
  tips: z.array(ToneTipSchema),
});
export type ToneSection = z.infer<typeof ToneSectionSchema>;

/** How a person was credited with their calls this week. */
export const AnsweredBySchema = z.object({
  intro: Count,
  login: Count,
  extension: Count,
  /** Credited by the desk only (no intro heard, or a weak match): "unverified". */
  unverified: Count,
  /**
   * Of `intro`: the name fit one person at the office, but no time clock data
   * confirmed they were in ("matched by name"). Older stored cards: 0.
   */
  byName: Count.default(0),
  /** Answered calls with a transcript, and how many had the staff member say their name. */
  introduced: RatioSchema,
});

export const CardStatusSchema = z.enum(['ok', 'not_enough', 'no_desk', 'not_call_role']);

export const ReportCardSchema = z.object({
  profileId: Uuid,
  name: z.string(),
  role: CoachingRoleSchema,
  roleLabel: z.string(),
  locationId: Uuid,
  officeName: z.string(),
  week: IsoDate,
  weekEnd: IsoDate,
  status: CardStatusSchema,
  /** Behaviour grade (the parts with enough chances); null under MIN_CHANCES. */
  behaviourScore: z.number().min(0).max(100).nullable(),
  behaviourGrade: z.string().nullable(),
  /** Chances across all parts this week. */
  chances: Count,
  behaviours: z.array(BehaviourRowSchema),
  results: z.array(ResultRowSchema),
  /** Phones, Booking, Saving the schedule, Checkout, Accuracy (stored cards before 2026-11-03 have none). */
  pillars: z.array(PillarSchema).default([]),
  /** Call tone beside caller sentiment (tips quote the call: the person, their lead and all-office only). */
  tone: ToneSectionSchema.nullable().default(null),
  answeredBy: AnsweredBySchema.nullable().default(null),
  /** The habit being worked on (chosen from an earlier week), with its check-back. */
  habit: HabitSchema.nullable(),
  /** One strong moment this week (their own call). */
  win: z.object({ part: z.enum(QA_PARTS), habit: z.string(), receipt: ReceiptSchema }).nullable(),
  /** A coworker's strong call on the habit (only from people who opted in). */
  example: z
    .object({ part: z.enum(QA_PARTS), coworker: z.string(), receipt: ReceiptSchema })
    .nullable(),
  /** Days and hours on the time clock this week; null without a clock link. */
  shift: z.object({ days: Count, hours: z.number().nonnegative() }).nullable(),
  /** The numbers Phones > Call analytics shows for the same desk(s) and week. */
  desk: z.object({
    answered: Count,
    answeredOpen: Count,
    outbound: Count,
    booked: Count,
    missedOnShift: Count,
    missedOnShiftTruly: Count,
    reviewed: Count,
    qaScore: z.number().nullable(),
    medianSpeedSeconds: z.number().nullable(),
  }),
  notes: z.array(z.string()),
  openDisputes: Count,
  /** "desk phone 1062 + Dentrix", "Dentrix only": the sources this card was graded from. */
  gradedFrom: z.string().nullable().default(null),
  /**
   * Set while the week is still open (Monday to now): the card is the week so
   * far, and the Friday close (`closes`, e.g. "Friday 3 PM") is the official grade.
   */
  soFar: z.object({ closes: z.string(), asOf: Iso }).nullable().default(null),
});
export type ReportCard = z.infer<typeof ReportCardSchema>;

/**
 * The overall grade of a card (the weekly award's points): the average of the
 * graded pillars, each already per hour and per real chance, needing two
 * graded pillars or a graded Phones pillar; else "not enough yet" (null).
 */
export function overallPoints(card: Pick<ReportCard, 'pillars'>): number | null {
  const graded = (card.pillars ?? []).filter((p) => p.score !== null);
  const phones = graded.some((p) => p.key === 'phones');
  if (graded.length === 0 || (graded.length < 2 && !phones)) return null;
  return Math.round((graded.reduce((n, p) => n + (p.score ?? 0), 0) / graded.length) * 10) / 10;
}

export function overallGrade(card: Pick<ReportCard, 'pillars'>): string | null {
  const p = overallPoints(card);
  return p === null ? null : letterGrade(Math.round(p));
}

/** "Friday 3 PM" from the office's Friday send hour (pure). */
export function closesLabel(sendHour: number): string {
  const h = sendHour % 12 === 0 ? 12 : sendHour % 12;
  return `Friday ${h} ${sendHour < 12 ? 'AM' : 'PM'}`;
}

/* ------------------------------------------------------------------------ */
/* Notes to staff (Office Manager, 2026-11-03): in the app only              */
/* ------------------------------------------------------------------------ */

export const NOTE_KINDS = ['coaching', 'praise'] as const;
export const StaffNoteSchema = z.object({
  id: Uuid,
  profileId: Uuid,
  kind: z.enum(NOTE_KINDS),
  body: z.string(),
  fromName: z.string(),
  createdAt: Iso,
  readAt: Iso.nullable(),
});
export type StaffNote = z.infer<typeof StaffNoteSchema>;

export const CardQuerySchema = z.object({
  week: IsoDate.optional(),
  profileId: Uuid.optional(),
});

/** The weekly award: only the winner is ever shown (never a list, never a bottom). */
export const AwardSchema = z.object({
  week: IsoDate,
  locationId: Uuid,
  officeName: z.string(),
  role: CoachingRoleSchema,
  roleLabel: z.string(),
  profileId: Uuid,
  name: z.string(),
  points: z.number(),
  reason: z.string().nullable(),
  /** The viewer won it. */
  mine: z.boolean(),
});
export type Award = z.infer<typeof AwardSchema>;

export const MyCoachingSchema = z.object({
  profile: CoachingProfileSchema.nullable(),
  card: ReportCardSchema.nullable(),
  history: z.array(HabitSchema),
  /** Weeks with a stored card, newest first. */
  weeks: z.array(IsoDate),
  /** The latest award at the person's office for their role. */
  award: AwardSchema.nullable().default(null),
  /** Notes from the owner or office lead (newest first, last 30 days). */
  notes: z.array(StaffNoteSchema).default([]),
  /** What this person may open beyond their own card. */
  access: z.object({
    leadOf: z.array(Uuid),
    allOffices: z.boolean(),
    canManage: z.boolean(),
  }),
});
export type MyCoaching = z.infer<typeof MyCoachingSchema>;

export const TeamRowSchema = z.object({
  profileId: Uuid,
  name: z.string(),
  role: CoachingRoleSchema,
  roleLabel: z.string(),
  locationId: Uuid,
  officeName: z.string(),
  status: CardStatusSchema,
  chances: Count,
  behaviourGrade: z.string().nullable(),
  /** The overall grade (graded pillars), null under the minimums ("not enough yet"). */
  overallGrade: z.string().nullable().default(null),
  /** Against their own last four weeks (never against coworkers). */
  trend: TrendSchema.nullable(),
  habit: z.string().nullable(),
  habitStatus: HabitStatusSchema.nullable(),
  weeksStuck: Count,
  openDisputes: Count,
  /** Pillar grades (no ranking: shown per person, alphabetical rows). */
  pillars: z
    .array(
      z.object({
        key: z.enum(PILLAR_KEYS),
        grade: z.string().nullable(),
        state: MetricStateSchema,
      }),
    )
    .default([]),
});
export const TeamResponseSchema = z.object({
  week: IsoDate,
  weekEnd: IsoDate,
  /** Alphabetical: a working list, not a ranking. */
  rows: z.array(TeamRowSchema),
  /** This week's (or the last closed week's) winners per office and role. */
  awards: z.array(AwardSchema).default([]),
  notes: z.array(z.string()),
});
export type TeamResponse = z.infer<typeof TeamResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Clips                                                                     */
/* ------------------------------------------------------------------------ */

export const ClipRequestSchema = z.object({
  /** Which part's moment to start at (default: the start of the call). */
  part: z.enum(QA_PARTS).optional(),
  /** Or an exact moment (tone: where the overtalk or the silence was). */
  atMs: z
    .number()
    .int()
    .nonnegative()
    .max(4 * 3_600_000)
    .optional(),
});
export const ClipResponseSchema = z.object({
  token: z.string(),
  expiresAt: Iso,
  locationId: Uuid,
  startMs: z.number().int().nonnegative(),
});
export type ClipResponse = z.infer<typeof ClipResponseSchema>;

/* ------------------------------------------------------------------------ */
/* "I disagree"                                                              */
/* ------------------------------------------------------------------------ */

export const DisputeCreateSchema = z.object({
  callId: Uuid,
  /** Null: the whole call's grades. */
  part: z.enum(QA_PARTS).nullable().default(null),
  reason: z.string().trim().min(3).max(500),
});
export const DisputeDecisionSchema = z.object({
  decision: z.enum(['upheld', 'overturned']),
  note: z.string().trim().max(500).nullable().default(null),
});
export const DisputeSchema = z.object({
  id: Uuid,
  locationId: Uuid,
  officeName: z.string(),
  profileId: Uuid,
  name: z.string(),
  callId: Uuid,
  callAt: Iso,
  part: z.enum(QA_PARTS).nullable(),
  partLabel: z.string(),
  /** The AI review's scores on the call (0-5 per part). */
  ai: QaScorecardSchema.nullable(),
  reason: z.string(),
  status: z.enum(['open', 'upheld', 'overturned']),
  decidedByName: z.string().nullable(),
  decidedAt: Iso.nullable(),
  note: z.string().nullable(),
  createdAt: Iso,
  canDecide: z.boolean(),
});
export type Dispute = z.infer<typeof DisputeSchema>;
export const DisputeListSchema = z.object({ items: z.array(DisputeSchema) });

/* ------------------------------------------------------------------------ */
/* Calibration                                                               */
/* ------------------------------------------------------------------------ */

export const CalibrationItemSchema = z.object({
  id: Uuid,
  callId: Uuid,
  callAt: Iso,
  locationId: Uuid,
  hasRecording: z.boolean(),
  /** Null until the lead grades the call (blind: the AI's scores stay hidden till then). */
  human: QaScorecardSchema.nullable(),
  ai: QaScorecardSchema.nullable(),
  gradedByName: z.string().nullable(),
  gradedAt: Iso.nullable(),
});
export const CalibrationOfficeSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  items: z.array(CalibrationItemSchema),
  graded: Count,
  /** Parts where human and AI were within CALIBRATION_AGREE_WITHIN, of parts both scored. */
  agreement: RatioSchema,
  perPart: z.array(
    z.object({
      part: z.enum(QA_PARTS),
      label: z.string(),
      agreement: RatioSchema,
      /** Average (AI - human); positive = the AI grades higher. */
      meanDiff: z.number().nullable(),
    }),
  ),
  canGrade: z.boolean(),
});
export const CalibrationResponseSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  offices: z.array(CalibrationOfficeSchema),
});
export type CalibrationResponse = z.infer<typeof CalibrationResponseSchema>;
export const CalibrationGradeSchema = z.object({ human: QaScorecardSchema });

/* ------------------------------------------------------------------------ */
/* Escalations and praise (read by the Council's Team advisor)              */
/* ------------------------------------------------------------------------ */

export const ESCALATION_KINDS = ['habit_stuck', 'rude_call', 'complaint', 'praise'] as const;
export const EscalationKindSchema = z.enum(ESCALATION_KINDS);
export type EscalationKind = z.infer<typeof EscalationKindSchema>;
export const ESCALATION_KIND_LABEL: Record<EscalationKind, string> = {
  habit_stuck: 'Habit still missing after coaching',
  rude_call: 'Rude or unsafe call',
  complaint: 'Patient complaint on a call',
  praise: 'Praise worth giving',
};

export const EscalationSchema = z.object({
  id: Uuid,
  kind: EscalationKindSchema,
  kindLabel: z.string(),
  locationId: Uuid,
  officeName: z.string(),
  profileId: Uuid.nullable(),
  name: z.string().nullable(),
  /** One plain line, counts only (no patient details). */
  summary: z.string(),
  /** Receipts (open with the clip; staff names only, never patients). */
  callIds: z.array(Uuid),
  week: IsoDate.nullable(),
  status: z.enum(['open', 'acknowledged', 'closed']),
  createdAt: Iso,
  handledByName: z.string().nullable(),
  handledAt: Iso.nullable(),
  note: z.string().nullable(),
});
export type Escalation = z.infer<typeof EscalationSchema>;

export const EscalationQuerySchema = z.object({
  /** Default 30 days. */
  days: z.coerce.number().int().min(1).max(180).default(30),
  status: z.enum(['open', 'all']).default('open'),
});
export const EscalationListSchema = z.object({
  items: z.array(EscalationSchema),
  /** Per office counts for the Council's Team advisor. */
  byOffice: z.array(
    z.object({
      locationId: Uuid,
      officeName: z.string(),
      open: Count,
      habitStuck: Count,
      rudeOrComplaint: Count,
      praise: Count,
    }),
  ),
});
export type EscalationList = z.infer<typeof EscalationListSchema>;
export const EscalationActionSchema = z.object({
  status: z.enum(['acknowledged', 'closed']),
  note: z.string().trim().max(500).nullable().default(null),
});

/* ------------------------------------------------------------------------ */
/* Morning huddle note                                                       */
/* ------------------------------------------------------------------------ */

export const HuddleCallSchema = z.object({
  kind: z.enum(['voicemail', 'treatment', 'balance']),
  /** Patient (treatment, balance) or the voicemail's call. */
  patientId: Uuid.nullable(),
  callId: Uuid.nullable(),
  /** Display name (PHI: patient.view at the office only; else null). */
  name: z.string().nullable(),
  /** Dollars waiting (treatment value or balance), null for voicemails or without money.view. */
  cents: z.number().int().nullable(),
  detail: z.string(),
});
export const HuddleNoteSchema = z.object({
  profileId: Uuid,
  day: IsoDate,
  firstName: z.string(),
  locationId: Uuid,
  officeName: z.string(),
  win: z.object({ part: z.enum(QA_PARTS), habit: z.string(), receipt: ReceiptSchema }).nullable(),
  /** One thing to try today, tied to this week's habit. */
  tryToday: z
    .object({
      part: z.enum(QA_PARTS),
      habit: z.string(),
      text: z.string(),
      lesson: z.string(),
      /** The AI review's coaching tip from their own weakest call on the habit (PHI: patient.view only). */
      fromCall: z.string().nullable(),
    })
    .nullable(),
  calls: z.array(HuddleCallSchema),
  callsTotal: Count,
  generatedAt: Iso,
});
export type HuddleNote = z.infer<typeof HuddleNoteSchema>;
export const HuddleResponseSchema = z.object({
  note: HuddleNoteSchema.nullable(),
  /** Unread notes from the owner or office lead, and any from the last 2 days. */
  notes: z.array(StaffNoteSchema).default([]),
});

/* ------------------------------------------------------------------------ */
/* Settings and the Friday email                                             */
/* ------------------------------------------------------------------------ */

export const CoachingOfficeSettingSchema = z.object({
  locationId: Uuid,
  officeName: z.string(),
  /** In-app morning note on Home (never leaves Velora). */
  morningNote: z.boolean(),
  /** Friday report card emails. OFF by default; also needs Settings > Automations (Staff reports). */
  fridayEmail: z.boolean(),
  /** Office leads (lead queue, calibration, copies of the Friday email). */
  leadUserIds: z.array(Uuid),
  ccLeads: z.boolean(),
  /** Local hour on Friday the email goes out (default 15). */
  sendHour: z.number().int().min(6).max(20),
  /** Monthly AI-written report to the office leads. OFF by default; also needs Staff reports. */
  monthlyReport: z.boolean(),
  /** personal / shared / unknown: whether the PMS has one login per person. */
  pmsLoginQuality: z.enum(['personal', 'shared', 'unknown']),
});
export type CoachingOfficeSetting = z.infer<typeof CoachingOfficeSettingSchema>;

export const CoachingSettingsSchema = z.object({
  offices: z.array(CoachingOfficeSettingSchema),
  /** People who see every office's coaching (besides owners with Team & HR everywhere). */
  allOfficeUserIds: z.array(Uuid),
  /** Email sign-off: "Velora, for <teamName>". */
  teamName: z.string().nullable(),
  /** Friday emails start on this date (null: not yet; the first week runs in the app only). */
  emailsStartOn: IsoDate.nullable(),
  retention: z.object({
    /** Chair audio deleted this many days after the note is signed and the grade is set. */
    chairAudioDays: z.number().int(),
    /** Call audio kept in callvault (policy only: Velora never deletes callvault audio). */
    callAudioDays: z.number().int(),
    /** Transcripts and scores kept. */
    transcriptDays: z.number().int(),
  }),
  users: z.array(z.object({ id: Uuid, name: z.string() })),
  /** Settings > Automations & notifications: is "Staff reports" on, and why not. */
  gate: z.object({ allowed: z.boolean(), reason: z.string().nullable() }),
  canManage: z.boolean(),
});
export type CoachingSettings = z.infer<typeof CoachingSettingsSchema>;

export const CoachingSettingsPatchSchema = z.object({
  offices: z
    .array(
      z.object({
        locationId: Uuid,
        morningNote: z.boolean().optional(),
        fridayEmail: z.boolean().optional(),
        leadUserIds: z.array(Uuid).max(10).optional(),
        ccLeads: z.boolean().optional(),
        sendHour: z.number().int().min(6).max(20).optional(),
        monthlyReport: z.boolean().optional(),
        pmsLoginQuality: z.enum(['personal', 'shared', 'unknown']).optional(),
      }),
    )
    .max(50)
    .default([]),
  allOfficeUserIds: z.array(Uuid).max(20).optional(),
  teamName: z.string().trim().min(1).max(80).nullable().optional(),
  emailsStartOn: IsoDate.nullable().optional(),
  retention: z
    .object({
      chairAudioDays: z.number().int().min(1).max(365).optional(),
      callAudioDays: z.number().int().min(30).max(3650).optional(),
      transcriptDays: z.number().int().min(365).max(3650).optional(),
    })
    .optional(),
});

/* ------------------------------------------------------------------------ */
/* Chair recording pilot (software side)                                     */
/* ------------------------------------------------------------------------ */

export const CHAIR_PRESENTATION_PARTS = [
  'explains_why',
  'shows_image',
  'cost_clear',
  'offers_financing',
  'asks_appointment',
] as const;
export const CHAIR_DISMISSAL_PARTS = [
  'reinforces_plan',
  'post_op',
  'next_step_named',
  'walks_to_scheduling',
] as const;
export const CHAIR_PART_LABEL: Record<string, string> = {
  explains_why: 'Explains why the treatment is needed',
  shows_image: 'Shows the X-ray or photo',
  cost_clear: 'Gives the cost clearly',
  offers_financing: 'Offers financing when cost is the worry',
  asks_appointment: 'Asks for the appointment',
  reinforces_plan: "Reinforces the doctor's plan",
  post_op: 'Gives post-op instructions',
  next_step_named: 'Names the next step',
  walks_to_scheduling: 'Walks the patient to scheduling',
};
export const ChairParticipantsSchema = z.object({
  participants: z
    .array(z.object({ profileId: Uuid, speaker: z.enum(['provider', 'hygienist', 'assistant']) }))
    .max(3),
});
export const ChairGradeViewSchema = z.object({
  sessionId: Uuid,
  kind: z.enum(['presentation', 'dismissal']),
  visitOn: IsoDate,
  score: z.number().nullable(),
  parts: z.array(
    z.object({
      part: z.string(),
      label: z.string(),
      score: z.number().int().min(0).max(5).nullable(),
      speaker: z.string().nullable(),
      profileName: z.string().nullable(),
    }),
  ),
  /** The treatment planned at that visit was scheduled or done within 30 days. */
  accepted: z.boolean().nullable(),
});
export const ChairGradesSchema = z.object({ items: z.array(ChairGradeViewSchema) });

export const EmailPreviewSchema = z.object({
  profileId: Uuid,
  to: z.string().nullable(),
  cc: z.array(z.string()),
  subject: z.string(),
  text: z.string(),
  /** Why it would not send (not enough chances, no email address); null when it would. */
  skip: z.string().nullable(),
});
export type EmailPreview = z.infer<typeof EmailPreviewSchema>;
export const EmailTestResponseSchema = z.object({
  status: z.enum(['sent', 'held', 'failed']),
  reason: z.string().nullable(),
  to: z.string(),
});

export const MonthlyPreviewSchema = z.object({
  locationId: Uuid,
  month: z.string(),
  to: z.array(z.string()),
  subject: z.string(),
  text: z.string(),
  /** Written by the AI (OpenAI under the BAA) from counts and grades only; else the plain summary. */
  aiWritten: z.boolean(),
  skip: z.string().nullable(),
});
export type MonthlyPreview = z.infer<typeof MonthlyPreviewSchema>;

export const EmailLogSchema = z.object({
  items: z.array(
    z.object({
      profileId: Uuid,
      name: z.string(),
      week: IsoDate,
      status: z.enum(['sent', 'held', 'skipped', 'failed']),
      reason: z.string().nullable(),
      at: Iso,
    }),
  ),
});

export const RunWeekSchema = z.object({ week: IsoDate.optional() });
export const RunWeekResultSchema = z.object({
  week: IsoDate,
  cards: Count,
  habits: Count,
  escalations: Count,
  emails: z.object({ sent: Count, held: Count, skipped: Count }),
});
